import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  BookOpen, 
  Layers, 
  Plus, 
  ChevronRight, 
  ChevronDown,
  MoreHorizontal,
  Link as LinkIcon,
  CheckCircle2,
  Clock,
  FileText,
  GraduationCap,
  ScrollText,
  PieChart,
  Target,
  CalendarDays,
  Download,
  AlertTriangle,
  School,
  ExternalLink,
  NotebookPen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAllClassesWithAssessments } from '../../data/classAssessments';
import { getCurrentUser, filterClassesByUser } from '@/data/users';
import apiService from '@/services/api';
import { DynamicPlannerTab, DynamicSchemeTab, DynamicBlueprintsTab } from '@/components/curriculum/DynamicCurriculumTabs';
// EmbeddedLessonPlans removed - each subject has its own Lesson Plans tab now
import EmbeddedYearlyPlanner from '@/components/curriculum/EmbeddedYearlyPlanner';
import EmbeddedSchemeOfWork from '@/components/curriculum/EmbeddedSchemeOfWork';
import EmbeddedUnitPlans from '@/components/curriculum/EmbeddedUnitPlans';

export default function CurriculumPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  
  // State for actual teacher classes from API
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [curriculumDataAPI, setCurriculumDataAPI] = useState(null);
  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  
  // Filter classes based on user permissions (using hardcoded data initially)
  const allClasses = getAllClassesWithAssessments();
  const accessibleClasses = filterClassesByUser(allClasses, currentUser);
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "planner");
  const [activeTerm, setActiveTerm] = useState("term1");
  const [selectedBlueprint, setSelectedBlueprint] = useState(searchParams.get('blueprint') || "unit11_test");
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [selectedClass, setSelectedClass] = useState(""); // Start empty, will be set after API fetch
  
  // Fetch teacher's actual classes from API and filter hardcoded blueprints
  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        setLoading(true);
        const classes = await apiService.getTeacherClasses();
        setTeacherClasses(classes);
        
        // Set first class as selected (use API classes directly)
        if (classes.length > 0) {
          setSelectedClass(classes[0].name);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser && currentUser.role === 'teacher') {
      fetchTeacherClasses();
    } else {
      setLoading(false);
      // For non-teachers, use first accessible class
      if (accessibleClasses.length > 0) {
        const firstClass = typeof accessibleClasses[0] === 'string' 
          ? accessibleClasses[0] 
          : (accessibleClasses[0].title || accessibleClasses[0].name || accessibleClasses[0].className);
        setSelectedClass(firstClass);
      }
    }
  }, []);

  // Fetch curriculum data when selected class changes
  useEffect(() => {
    const fetchCurriculumData = async () => {
      if (!selectedClass || !teacherClasses.length) return;
      
      try {
        setLoadingCurriculum(true);
        // Find the class ID from the selected class name
        const classObj = teacherClasses.find(c => c.name === selectedClass);
        if (classObj) {
          const data = await apiService.getCurriculumData(classObj.id);
          setCurriculumDataAPI(data);
        }
      } catch (error) {
        console.error('Error fetching curriculum data:', error);
      } finally {
        setLoadingCurriculum(false);
      }
    };
    
    fetchCurriculumData();
  }, [selectedClass, teacherClasses]);

  // Update tab when URL parameter changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    const blueprint = searchParams.get('blueprint');
    if (tab) setActiveTab(tab);
    if (blueprint) setSelectedBlueprint(blueprint);
  }, [searchParams]);

  // Unit content data structure
  const unitContent = {
    '1.1': {
      hasContent: true,
      title: 'Future ICT Trends and Their Key Features',
      weeks: '1-2',
      sessions: 2,
      notes: {
        overview: 'Understanding key future trends in digital technologies and the Internet, with focus on education, health, and home applications in Vanuatu context.',
        keyTopics: [
          'AI-powered personalized learning platforms',
          'Telehealth and remote patient monitoring',
          'Smart home ecosystems powered by IoT'
        ]
      }
    },
    '1.2': {
      hasContent: true,
      title: 'Management of Information Systems in Vanuatu',
      weeks: '3-4',
      sessions: 2,
      notes: {
        overview: 'Understanding workflows, Information Systems applications, IS development processes, and managing IS implementations in the Vanuatu context with focus on local government, education, and tourism sectors.',
        keyTopics: [
          'Workflows and their components in Vanuatu organizations',
          'Information Systems applications and benefits',
          'IS development lifecycle (Waterfall model)',
          'Managing and implementing IS in Pacific Island Countries',
          'Student Information Systems (SIS)',
          'Tourism Management Systems',
          'Context-specific challenges: infrastructure, skills, cost'
        ]
      }
    },
    '1.3': {
      hasContent: false,
      title: 'Digital Media Concepts',
      weeks: '5-6',
      sessions: 2,
      notes: {
        overview: 'Understand fundamental concepts of digital media including resolution, color depth, compression, and file formats.',
        keyTopics: [
          'Digital media fundamentals (bitmap vs vector)',
          'Resolution and color depth',
          'Compression techniques (lossy vs lossless)',
          'File format standards and conversions'
        ]
      }
    },
    '1.4': {
      hasContent: false,
      title: 'Graphics Cards & Processing',
      weeks: '7',
      sessions: 1,
      notes: {
        overview: 'Understand graphics design concepts and hardware requirements for digital media processing.',
        keyTopics: [
          'Graphics card architecture and specifications',
          'GPU vs CPU processing',
          'Video memory and bandwidth',
          'Graphics processing for digital media'
        ]
      }
    },
    '1.5': {
      hasContent: false,
      title: 'Video Processing Concepts',
      weeks: '8',
      sessions: 1,
      notes: {
        overview: 'Understand video processing concepts including frame rates, codecs, and editing techniques.',
        keyTopics: [
          'Video formats and codecs',
          'Frame rates and resolution',
          'Video editing fundamentals',
          'Rendering and export settings'
        ]
      }
    },
    '1.6': {
      hasContent: false,
      title: 'Audio Processing Concepts',
      weeks: '9',
      sessions: 1,
      notes: {
        overview: 'Understand audio processing concepts including sample rates, bit depth, and audio editing.',
        keyTopics: [
          'Digital audio fundamentals',
          'Sample rates and bit depth',
          'Audio file formats',
          'Basic audio editing and effects'
        ]
      }
    },
    '1.7': {
      hasContent: false,
      title: 'Digital Media Design Project',
      weeks: '10-12',
      sessions: 3,
      notes: {
        overview: 'Master basic skills for designing digital graphics, video, and audio through a comprehensive project.',
        keyTopics: [
          'Project planning and storyboarding',
          'Creating digital graphics',
          'Video production and editing',
          'Audio production and integration',
          'Final project presentation'
        ]
      }
    }
  };

  const toggleUnit = (unitId) => {
    setExpandedUnit(expandedUnit === unitId ? null : unitId);
  };
  
  // UNIFIED DATA STRUCTURE: Links Year Planner and Scheme of Work
  // Changes to plannerData automatically reflect in schemeTopics
  const [curriculumData, setCurriculumData] = useState({
    term1: {
      weeks: 13,
      planner: [
        { week: 1, date: "Jan 26 - Jan 30", event: "Syllabus & IA Brief", type: "Admin", strandId: 1, topic: "Brief on Syllabus and IA" },
        { week: 2, date: "Feb 02 - Feb 06", event: "ICT Trends in Education", type: "Lesson", strandId: 1, topic: "Trends of ICT in Education" },
        { week: 3, date: "Feb 09 - Feb 13", event: "Digital Media Applications", type: "Lesson", strandId: 1, topic: "Digital Media Applications & Sources" },
        { week: 4, date: "Feb 16 - Feb 20", event: "Digital Media Sources", type: "Lesson", strandId: 1, topic: "Digital Media Applications & Sources" },
        { week: 5, date: "Feb 23 - Feb 27", event: "Digital Media Concepts", type: "Lesson", strandId: 1, topic: "Digital Media Concepts" },
        { week: 6, date: "Mar 02 - Mar 06", event: "Digital Media Concepts", type: "Lesson", strandId: 1, topic: "Digital Media Concepts" },
        { week: 7, date: "Mar 09 - Mar 13", event: "Graphics Cards & Processing", type: "Lesson", strandId: 1, topic: "Graphics Cards" },
        { week: 8, date: "Mar 16 - Mar 20", event: "Video Processing", type: "Lesson", strandId: 1, topic: "Video Concepts" },
        { week: 9, date: "Mar 23 - Mar 27", event: "Audio Processing", type: "Lesson", strandId: 1, topic: "Audio Concepts" },
        { week: 10, date: "Mar 30 - Apr 03", event: "Digital Media Design", type: "Lesson", strandId: 1, topic: "Digital Graphics Design" },
        { week: 11, date: "Apr 06 - Apr 10", event: "Video Design & Processing", type: "Lesson", strandId: 1, topic: "Digital Video Processing" },
        { week: 12, date: "Apr 13 - Apr 17", event: "Audio Design & Processing", type: "Lesson", strandId: 1, topic: "Digital Audio Processing" },
        { week: 13, date: "Apr 20 - Apr 24", event: "Termly Test - Strand 1", type: "Assessment", strandId: 1, assessmentType: "Test" },
        { week: "Break", date: "Apr 27 - May 01", event: "Term 1 Holidays (1 Week)", type: "Holiday" },
      ],
      strands: [
        {
          id: 1,
          title: "Strand 1: Personal Computer Systems",
          subtitle: "Digital Media & ICT Trends",
          status: "In Progress"
        },
        {
          id: 2,
          title: "Strand 2: Website Development",
          subtitle: "Web Design & Database-Driven Websites",
          status: "Planned"
        },
        {
          id: 3,
          title: "Strand 3: Programming",
          subtitle: "Problem Solving & Programming Stages",
          status: "Planned"
        }
      ]
    },
    term2: {
      weeks: 12,
      planner: [
        { week: 1, date: "May 04 - May 08", event: "Web Design Concepts", type: "Lesson", strandId: 2, topic: "Web Design" },
        { week: 2, date: "May 11 - May 15", event: "Database-Driven Websites", type: "Lesson", strandId: 2, topic: "Database Basics" },
        { week: 3, date: "May 18 - May 22", event: "Creating DB Websites", type: "Lesson", strandId: 2, topic: "Database Creation" },
        { week: 4, date: "May 25 - May 29", event: "Problem Solving", type: "Lesson", strandId: 3, topic: "Problem Solving Concepts" },
        { week: 5, date: "Jun 01 - Jun 05", event: "Software Design", type: "Lesson", strandId: 3, topic: "Software Design Approaches" },
        { week: 6, date: "Jun 08 - Jun 12", event: "Programming Stages", type: "Lesson", strandId: 3, topic: "Stages of Programming" },
        { week: 7, date: "Jun 15 - Jun 19", event: "IA Draft 2 Work", type: "Assessment", strandId: 3, assessmentType: "IA" },
        { week: 8, date: "Jun 22 - Jun 26", event: "Coding Practice", type: "Lesson", strandId: 3, topic: "Coding Competency" },
        { week: 9, date: "Jun 29 - Jul 03", event: "Microprocessors", type: "Lesson", strandId: 4, topic: "Programmable Microprocessors" },
        { week: 10, date: "Jul 06 - Jul 10", event: "Embedded Devices", type: "Lesson", strandId: 4, topic: "Embedded Device Control" },
        { week: 11, date: "Jul 13 - Jul 17", event: "Environmental Response", type: "Lesson", strandId: 4, topic: "Critical Environment Changes" },
        { week: 12, date: "Jul 20 - Jul 24", event: "Ethical & Social Issues", type: "Lesson", strandId: 5, topic: "Ethics in ICT" },
        { week: "Break", date: "Jul 27 - Aug 14", event: "Term 2 Holidays (3 Weeks)", type: "Holiday" },
      ],
      strands: [
        {
          id: 4,
          title: "Strand 4: Microprocessor Programming",
          subtitle: "Microprocessors & Embedded Devices",
          status: "Planned"
        },
        {
          id: 5,
          title: "Strand 5: Issues in ICT",
          subtitle: "Ethical, Social, Environmental & Health Issues",
          status: "Planned"
        }
      ]
    },
    term3: {
      weeks: 8,
      planner: [
        { week: 1, date: "Aug 17 - Aug 21", event: "Legislation & Measures", type: "Lesson", strandId: 5, topic: "ICT Legislation" },
        { week: 2, date: "Aug 24 - Aug 28", event: "Environmental Issues", type: "Lesson", strandId: 5, topic: "Environmental Concerns" },
        { week: 3, date: "Aug 31 - Sep 04", event: "E-waste Management", type: "Lesson", strandId: 5, topic: "E-waste & Power Usage" },
        { week: 4, date: "Sep 07 - Sep 11", event: "Health Issues", type: "Lesson", strandId: 5, topic: "Health & ICT Equipment" },
        { week: 5, date: "Sep 14 - Sep 18", event: "Ethics Case Study", type: "Assessment", strandId: 5, assessmentType: "Project" },
        { week: 6, date: "Sep 21 - Sep 25", event: "Revision Week", type: "Lesson", strandId: null, topic: "Review All Strands" },
        { week: 7, date: "Sep 28 - Oct 02", event: "Mock Examinations", type: "Assessment", strandId: null, assessmentType: "Exam" },
        { week: 8, date: "Oct 05 - Oct 09", event: "Final Preparations", type: "Lesson", strandId: null, topic: "Exam Prep" },
        { week: "Break", date: "Oct 12 - Oct 16", event: "Term 3 Holidays (1 Week)", type: "Holiday" },
      ],
      strands: []
    },
    term4: {
      weeks: 8,
      planner: [
        { week: 1, date: "Oct 19 - Oct 23", event: "Portfolio Review", type: "Assessment", strandId: null, assessmentType: "IA" },
        { week: 2, date: "Oct 26 - Oct 30", event: "IA Final Submission", type: "Assessment", strandId: null, assessmentType: "IA" },
        { week: 3, date: "Nov 02 - Nov 06", event: "Practical Assessments", type: "Assessment", strandId: null, assessmentType: "Practical" },
        { week: 4, date: "Nov 09 - Nov 13", event: "Project Presentations", type: "Assessment", strandId: null, assessmentType: "Project" },
        { week: 5, date: "Nov 16 - Nov 20", event: "Comprehensive Revision", type: "Lesson", strandId: null, topic: "Final Revision" },
        { week: 6, date: "Nov 23 - Nov 27", event: "Exam Techniques", type: "Lesson", strandId: null, topic: "Exam Strategies" },
        { week: 7, date: "Nov 30 - Dec 04", event: "Final Examinations", type: "Assessment", strandId: null, assessmentType: "Exam" },
        { week: 8, date: "Dec 07 - Dec 11", event: "Year End Wrap-up", type: "Admin", strandId: null, topic: "Course Completion" },
        { week: "End", date: "Dec 15 onwards", event: "Annual Leave", type: "Holiday" },
      ],
      strands: []
    }
  });

  // Generate plannerData from curriculumData for backward compatibility
  const plannerData = {
    term1: curriculumData.term1.planner,
    term2: curriculumData.term2.planner,
    term3: curriculumData.term3.planner,
    term4: curriculumData.term4.planner
  };

  // DYNAMICALLY GENERATE SCHEME OF WORK FROM CURRICULUM DATA
  // This ensures Year Planner and Scheme of Work are always synchronized
  const generateSchemeTopics = () => {
    const topics = [];
    const strandDetails = {
      1: {
        strand: "Strand 1: Personal Computer Systems",
        title: "Digital Media & ICT Trends",
        outcomes: [
          "Demonstrate understanding of key future trends in digital technologies and Internet",
          "Identify and install appropriate media applications",
          "Understand digital media concepts",
          "Understand graphics design concepts and requirements",
          "Understand video and audio processing concepts",
          "Master basic skills for designing digital graphics, video, and audio"
        ],
        assessments: [
          { name: "IA Draft 1", type: "IA" },
          { name: "Moodle Activities", type: "Exercises" },
          { name: "Termly Test - Strand 1", type: "Test" }
        ],
        resources: "Computer, Smart phone, Projector, Internet, Android devices, Flash memory",
        status: "In Progress"
      },
      2: {
        strand: "Strand 2: Website Development",
        title: "Web Design & Database-Driven Websites",
        outcomes: [
          "Understand web design concepts and website development with media objects",
          "Understand basic concepts of database-driven websites",
          "Create functional database-driven websites"
        ],
        assessments: [
          { name: "Moodle Activities", type: "Exercises" },
          { name: "Website Development Project", type: "Project" }
        ],
        resources: "Computer, Internet, Web development tools",
        status: "Planned"
      },
      3: {
        strand: "Strand 3: Programming",
        title: "Problem Solving & Programming Stages",
        outcomes: [
          "Understand concepts of problem-solving",
          "Use software design approaches effectively",
          "Understand basic concepts for designing and constructing program solutions",
          "Demonstrate competency in coding"
        ],
        assessments: [
          { name: "IA Draft 2", type: "IA" },
          { name: "Coding Exercises", type: "Exercises" },
          { name: "Programming Test", type: "Test" }
        ],
        resources: "Computer, Programming IDE, Moodle",
        status: "Planned"
      },
      4: {
        strand: "Strand 4: Microprocessor Programming",
        title: "Microprocessors & Embedded Devices",
        outcomes: [
          "Understand concepts of microprocessors",
          "Use programmable microprocessors to control embedded devices",
          "Develop systems to respond to critical changes in physical environment"
        ],
        assessments: [
          { name: "Microprocessor Project", type: "Project" },
          { name: "Embedded Systems Demo", type: "Practical" }
        ],
        resources: "Computer, Microprocessor kits, Sensors",
        status: "Planned"
      },
      5: {
        strand: "Strand 5: Issues in ICT",
        title: "Ethical, Social, Environmental & Health Issues",
        outcomes: [
          "Understand ethical concerns in relation to ICT use",
          "Know legislations and measures to minimize ethical issues",
          "Understand environmental concerns with ICT growth",
          "Understand effective e-waste management and power usage",
          "Understand health issues related to ICT equipment usage"
        ],
        assessments: [
          { name: "Ethics Case Study", type: "Project" },
          { name: "Issues in ICT Test", type: "Test" }
        ],
        resources: "Computer, Internet, Research materials",
        status: "Planned"
      }
    };

    // Generate topics for each strand by extracting from planner data
    Object.keys(curriculumData).forEach((termKey) => {
      const termData = curriculumData[termKey];
      const termNumber = termKey.replace('term', '');
      
      // Group planner items by strand
      const strandGroups = {};
      termData.planner.forEach(item => {
        if (item.strandId && item.type === 'Lesson') {
          if (!strandGroups[item.strandId]) {
            strandGroups[item.strandId] = [];
          }
          strandGroups[item.strandId].push(item);
        }
      });

      // Create scheme topics for each strand
      Object.keys(strandGroups).forEach(strandId => {
        const strandInt = parseInt(strandId);
        const strandItems = strandGroups[strandId];
        const detail = strandDetails[strandInt];
        
        if (detail && !topics.find(t => t.id === strandInt)) {
          const weekNumbers = strandItems.map(item => item.week);
          const weekRange = `Week ${Math.min(...weekNumbers)}-${Math.max(...weekNumbers)}`;
          
          topics.push({
            id: strandInt,
            term: `Term ${termNumber}`,
            strand: detail.strand,
            title: detail.title,
            weeks: weekRange,
            subtopics: strandItems.map(item => `${item.topic} (Week ${item.week})`),
            outcomes: detail.outcomes,
            assessments: detail.assessments,
            resources: detail.resources,
            status: detail.status
          });
        }
      });
    });

    return topics.sort((a, b) => a.id - b.id);
  };

  const schemeTopics = generateSchemeTopics();

  // Helper: Get current term data from API
  const getCurrentTermData = () => {
    if (!curriculumDataAPI || !curriculumDataAPI.terms) return null;
    const termIndex = parseInt(activeTerm.replace('term', '')) - 1;
    return curriculumDataAPI.terms[termIndex] || null;
  };

  // Helper: Group assessments by type for display
  const groupAssessmentsByType = (assessments) => {
    const grouped = {};
    assessments.forEach(assessment => {
      const type = assessment.type || 'other';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(assessment);
    });
    return grouped;
  };

  // Blueprint Data - Units 1.1 & 1.2
  const blueprints = {
    unit11_test: {
      id: "unit11_test",
      title: "Unit 1.1: Future ICT Trends Assessment Blueprint",
      type: "Formative Assessment",
      totalMarks: 15,
      duration: "30 mins",
      topics: ["Future ICT Trends", "Education", "Health", "Home Applications"],
      items: [
        { 
          id: 1, 
          topic: "Education ICT Trend", 
          outcome: "Identify a future ICT trend that can help in education", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 1, 
          marks: 2,
          sampleQuestion: "Which emerging ICT trend is expected to enhance personalized learning in classrooms? A) Blockchain B) AI-powered tutoring C) Quantum computing D) 5G networks",
          answer: "B) AI-powered tutoring"
        },
        { 
          id: 2, 
          topic: "Education Feature", 
          outcome: "State a key feature of the identified future ICT trend in education", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 3,
          sampleQuestion: "Briefly describe one major feature of AI-powered tutoring in education.",
          answer: "AI-powered tutoring adapts to each student's learning pace and style, providing personalized content and feedback."
        },
        { 
          id: 3, 
          topic: "Health ICT Trend", 
          outcome: "Identify a future ICT trend that can help with health", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 1, 
          marks: 2,
          sampleQuestion: "Which future ICT trend can improve remote healthcare services? A) Augmented Reality B) Telemedicine powered by AI C) Edge computing D) 3D printing",
          answer: "B) Telemedicine powered by AI"
        },
        { 
          id: 4, 
          topic: "Health Feature", 
          outcome: "State a key feature of the identified future ICT trend in health", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 3,
          sampleQuestion: "Mention one important feature of AI-powered telemedicine.",
          answer: "AI-powered telemedicine enables remote diagnosis and consultation, allowing patients to receive medical advice without traveling to a clinic."
        },
        { 
          id: 5, 
          topic: "Home ICT Trend", 
          outcome: "Identify a future ICT trend that can help at home", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 1, 
          marks: 2,
          sampleQuestion: "Which emerging ICT trend will enhance smart home automation? A) Internet of Things (IoT) B) Blockchain C) VR-enabled home management D) Quantum computing",
          answer: "A) Internet of Things (IoT)"
        },
        { 
          id: 6, 
          topic: "Home Feature", 
          outcome: "State a key feature of the identified future ICT trend at home", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 3,
          sampleQuestion: "Explain one key feature of IoT in smart home automation.",
          answer: "IoT connects household devices allowing them to communicate and be controlled remotely through a central system or smartphone."
        }
      ],
      distribution: { knowledge: 40, understanding: 60 },
      resources: ["Assessment Blueprint PDF", "Answer Key"]
    },
    unit11_exercises: {
      id: "unit11_exercises",
      title: "Unit 1.1: ICT Exercises",
      type: "Continuous Assessment",
      totalMarks: 35,
      duration: "60 mins",
      topics: ["Future ICT Trends", "AI", "Telehealth", "IoT", "Digital Literacy"],
      items: [
        {
          id: 1,
          topic: "Fill in the Blanks",
          outcome: "Identify key ICT terminology",
          level: "Knowledge",
          type: "Fill in the Blanks",
          count: 5,
          marks: 5,
          questions: [
            { q: "One future ICT trend that can personalize learning experiences in education is called ___.", a: "Artificial Intelligence (AI)" },
            { q: "A key feature of telehealth is the ability to provide medical consultations ___.", a: "remotely" },
            { q: "Smart home ecosystems rely on a network of connected devices known as the ___.", a: "Internet of Things (IoT)" },
            { q: "Faster digital connections in the future will be powered by technologies like ___.", a: "5G" },
            { q: "Developing ___ skills is crucial for Vanuatu to prepare for future ICT trends.", a: "digital literacy" }
          ]
        },
        {
          id: 2,
          topic: "Short Answer",
          outcome: "Apply ICT concepts to Vanuatu context",
          level: "Application",
          type: "Short Answer",
          count: 3,
          marks: 15,
          questions: [
            { 
              q: "Explain one way Augmented Reality (AR) could be used in education to benefit students in Vanuatu. (5 marks)",
              a: "AR could overlay digital information onto the real world, for example, allowing students to point their tablets at historical sites in Vanuatu and see virtual reconstructions or additional information about them. This could make learning more engaging and interactive."
            },
            { 
              q: "Describe one potential benefit of using AI-powered diagnostic tools in healthcare for the people of Vanuatu. (5 marks)",
              a: "AI could analyze medical images or patient data to help doctors diagnose illnesses more quickly and accurately, even in remote areas where specialist expertise might not be readily available. This could lead to earlier treatment and improved health outcomes."
            },
            { 
              q: "Give one example of how the Internet of Things (IoT) could improve efficiency or convenience in a home in Vanuatu. (5 marks)",
              a: "Smart sensors connected to the IoT could monitor energy usage in a home and automatically adjust appliances like air conditioners or lights to optimize energy consumption, leading to lower electricity bills."
            }
          ]
        },
        {
          id: 3,
          topic: "True or False",
          outcome: "Evaluate ICT statements",
          level: "Analysis",
          type: "True or False",
          count: 5,
          marks: 5,
          questions: [
            { q: "Future ICT trends are unlikely to have any negative impacts.", a: "False" },
            { q: "Improving internet infrastructure is important for Vanuatu to adopt future ICT trends.", a: "True" },
            { q: "Telehealth requires patients to travel to a physical clinic.", a: "False" },
            { q: "AI-powered personalized learning treats all students the same.", a: "False" },
            { q: "Cybersecurity is not a significant concern for Vanuatu as it embraces future ICTs.", a: "False" }
          ]
        },
        {
          id: 4,
          topic: "Discussion Question",
          outcome: "Synthesize recommendations for Vanuatu",
          level: "Synthesis",
          type: "Essay/Discussion",
          count: 1,
          marks: 10,
          question: "Imagine you are advising the government of Vanuatu on how to best prepare the country for the future of the internet. What are two key recommendations you would make, and why are these recommendations important for the future development of Vanuatu?",
          sampleAnswer: "1) Invest heavily in expanding affordable and reliable internet access across all islands. This is crucial to ensure all citizens can benefit from opportunities like online education and healthcare. 2) Develop comprehensive digital literacy training programs for all age groups. This ensures the population has skills to use new technologies effectively and safely."
        }
      ],
      distribution: { knowledge: 14, application: 43, analysis: 14, synthesis: 29 },
      resources: ["Exercise Worksheet", "Answer Key"]
    },
    unit12_test: {
      id: "unit12_test",
      title: "Unit 1.2: Management of Information Systems Test",
      type: "Formative Assessment",
      totalMarks: 60,
      duration: "90 mins",
      topics: ["Workflows", "IS Applications", "IS Development", "IS Management", "Vanuatu Context"],
      items: [
        { id: 1, topic: "Workflow Definition", outcome: "Define workflow concept", level: "Knowledge", type: "Multiple Choice", count: 10, marks: 10, sampleQuestion: "What is the primary purpose of a workflow? A) Create databases B) Define steps to achieve outcome C) Develop software D) Analyze reports", answer: "B" },
        { id: 2, topic: "IS Input Data", outcome: "Identify IS terminology", level: "Knowledge", type: "Fill in the Blanks", count: 5, marks: 10, questions: [
          { q: "Data that is entered into an information system is called ___", a: "Input" },
          { q: "Information Systems can be used to ___ repetitive tasks", a: "automate" },
          { q: "A system used to manage student information is a ___", a: "Student Information System (SIS)" },
          { q: "The ___ model is a sequential approach to system development", a: "Waterfall" },
          { q: "Ongoing ___ and ___ are crucial for information systems", a: "maintenance, updates" }
        ]},
        { id: 3, topic: "Workflow Components", outcome: "Describe workflow parts", level: "Understanding", type: "Short Answer", count: 4, marks: 20, sampleQuestion: "Describe the main parts or components of a typical workflow.", answer: "A typical workflow consists of inputs, activities/steps, decisions, outputs, and roles/responsibilities" },
        { id: 4, topic: "Tourism IS Application", outcome: "Apply IS to business scenario", level: "Application", type: "Application-Based", count: 1, marks: 10, sampleQuestion: "A tourism business struggles with manual bookings. Identify workflows that could be improved and suggest an IS solution.", answer: "Booking workflow and customer inquiry workflow could be improved with a Tourism Management System or CRM" },
        { id: 5, topic: "Vanuatu Context", outcome: "Evaluate context-specific factors", level: "Synthesis", type: "Discussion", count: 1, marks: 10, sampleQuestion: "Discuss importance of considering Vanuatu's context (infrastructure, culture, economy) when implementing IS.", answer: "Must consider limited connectivity, cost constraints, technical skills gap, cultural appropriateness, geographical distribution, and sustainability" }
      ],
      distribution: { knowledge: 33, understanding: 33, application: 17, synthesis: 17 },
      resources: ["Test Blueprint PDF", "Answer Key"]
    },
    unit12_exercises: {
      id: "unit12_exercises",
      title: "Unit 1.2: ICT Exercises",
      type: "Continuous Assessment",
      totalMarks: 50,
      duration: "60 mins",
      topics: ["Workflows", "IS Benefits", "IS Development", "IS Management"],
      items: [
        { id: 1, topic: "Workflow Definition", outcome: "Identify workflow concept", level: "Knowledge", type: "Multiple Choice", count: 5, marks: 10, questions: [
          { q: "Which best describes steps to complete a task?", a: "c) Workflow" },
          { q: "What is one benefit of improved student record management?", a: "b) Improved student record management" },
          { q: "What is key activity in IS development?", a: "c) Information Systems development" },
          { q: "What is potential challenge with IS?", a: "c) High costs of hardware and software" },
          { q: "What is one aspect of managing an IS?", a: "b) Managing an IS application" }
        ]},
        { id: 2, topic: "IS Terminology", outcome: "Complete IS terms", level: "Knowledge", type: "Fill in the Blanks", count: 5, marks: 10, questions: [
          { q: "Data entered into an IS is called ___", a: "Input" },
          { q: "IS can ___ repetitive tasks", a: "automate" },
          { q: "System for student info is ___", a: "Student Information System (SIS)" },
          { q: "The ___ model is sequential", a: "Waterfall" },
          { q: "Ongoing ___ and ___ are crucial", a: "maintenance, updates" }
        ]},
        { id: 3, topic: "Workflows in Practice", outcome: "Apply workflow concepts", level: "Application", type: "Short Answer", count: 5, marks: 15, questions: [
          { q: "State one workflow in local government (3 marks)", a: "Business license processing: Receive application → Review documents → Get approvals → Issue license" },
          { q: "Describe relationship between workflow and IS (4 marks)", a: "IS supports and automates workflow steps, manages data, tracks progress, generates reports" },
          { q: "Explain IS benefit in tourism (4 marks)", a: "Booking system allows online reservations, increases reach, more bookings and revenue" },
          { q: "Name two IS development steps (2 marks)", a: "Planning, Analysis, Design, Implementation, Maintenance (any two)" },
          { q: "State one IS management aspect (2 marks)", a: "Security, maintenance, user support, data integrity (any one)" }
        ]},
        { id: 4, topic: "Workflow Importance", outcome: "Synthesize workflow understanding", level: "Synthesis", type: "Discussion", count: 1, marks: 15, question: "Discuss importance of understanding existing workflows when developing new IS for Vanuatu organization.", sampleAnswer: "Ensures alignment with business needs and identifies areas for improvement. Understanding workflows helps develop relevant systems that meet real organizational needs." }
      ],
      distribution: { knowledge: 40, application: 30, synthesis: 30 },
      resources: ["Exercise Worksheet", "Answer Key"]
    },
    unit12_assignment: {
      id: "unit12_assignment",
      title: "Unit 1.2: ICT Assignment",
      type: "Internal Assessment",
      totalMarks: 60,
      duration: "120 mins",
      topics: ["Definitions", "IS Applications", "Vanuatu Scenarios"],
      items: [
        { id: 1, topic: "Workflow Definition", outcome: "Define workflow with example", level: "Understanding", type: "Definition", count: 1, marks: 5, question: "Define 'workflow' and provide school example in Vanuatu", answer: "Workflow is sequence of defined steps to achieve a goal. Example: Student absence reporting - Student informs teacher → Teacher records → Informs admin → Updates records" },
        { id: 2, topic: "IS Development", outcome: "Define IS development method", level: "Understanding", type: "Definition", count: 1, marks: 5, question: "Define 'IS development' and outline one method", answer: "IS development is creating, implementing, maintaining systems for organizational needs. Waterfall Model: linear sequential phases" },
        { id: 3, topic: "Data Management", outcome: "Define data management importance", level: "Understanding", type: "Definition", count: 1, marks: 5, question: "Define 'data management' and state importance for Vanuatu", answer: "Managing collection, storage, organization, security of data. Important for accurate decision-making and trust" },
        { id: 4, topic: "Workflow-IS Relationship", outcome: "Explain with government example", level: "Application", type: "Short Answer", count: 1, marks: 10, question: "Describe workflow-IS relationship. Explain how IS improves government workflow with example", answer: "IS supports and automates workflows. Example: Ministry of Education salary payments - automates calculations, tracks approvals, facilitates deposits" },
        { id: 5, topic: "Student Information System", outcome: "Outline SIS characteristics", level: "Application", type: "Short Answer", count: 1, marks: 9, question: "Outline three SIS characteristics and benefits", answer: "1) Student Record Management - centralized database, 2) Grading/Reporting - automated calculations, 3) Communication Tools - improved information flow" },
        { id: 6, topic: "PIC Challenges", outcome: "Identify challenges with solutions", level: "Analysis", type: "Short Answer", count: 1, marks: 6, question: "State two IS implementation challenges in PICs with mitigation strategies", answer: "1) Limited connectivity - use satellite/mobile broadband, 2) Lack of IT personnel - invest in training, partner with experts" },
        { id: 7, topic: "Vanuatu Adventures Scenario", outcome: "Apply IS to tourism business", level: "Application", type: "Scenario-Based", count: 1, marks: 20, question: "Tourism operator has manual booking issues. Identify IS type, three benefits, and implementation factor", answer: "Tourism Management System. Benefits: Reduced double-bookings, centralized customer info, streamlined scheduling. Factor: User training and support" }
      ],
      distribution: { understanding: 25, application: 58, analysis: 17 },
      resources: ["Assignment Document", "Answer Key", "Scenario Description"]
    },
    unit13_test: {
      id: "unit13_test",
      title: "Unit 1.3: ICT & Networking Fundamentals Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 100,
      duration: "2 hours",
      topics: ["ICT Infrastructure", "Networking", "Software Choices", "Software Installation"],
      items: [
        { 
          id: 1, 
          topic: "ICT Infrastructure", 
          outcome: "Identify an ICT infrastructure requirement", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 1, 
          marks: 2,
          sampleQuestion: "Which of the following is a basic ICT infrastructure requirement?",
          answer: "Reliable power supply, network connectivity, and hardware"
        },
        { 
          id: 2, 
          topic: "ICT Infrastructure", 
          outcome: "List the basic ICT infrastructure requirements", 
          level: "Knowledge", 
          type: "Short Answer", 
          count: 1, 
          marks: 3,
          sampleQuestion: "List three basic ICT infrastructure requirements for an organization.",
          answer: "1. Reliable internet connection, 2. Computer hardware, 3. Power backup system"
        },
        { 
          id: 3, 
          topic: "ICT Infrastructure", 
          outcome: "Sketch a key component of an ICT infrastructure", 
          level: "Application", 
          type: "Diagram", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Draw a diagram showing the basic network infrastructure for a small office.",
          answer: "Diagram should show: modem, router, switch, and connected devices"
        },
        { 
          id: 4, 
          topic: "ICT Infrastructure", 
          outcome: "Organize an ICT infrastructure plan", 
          level: "Critical Thinking", 
          type: "Case Study", 
          count: 1, 
          marks: 10,
          sampleQuestion: "Design an ICT infrastructure plan for a small school in Vanuatu.",
          answer: "Should include: needs assessment, budget, hardware/software requirements, maintenance plan"
        },
        { 
          id: 5, 
          topic: "Networking", 
          outcome: "State key features of LAN, WAN, MAN, Wi-Fi", 
          level: "Knowledge", 
          type: "Fill in the Blanks", 
          count: 1, 
          marks: 2,
          sampleQuestion: "A _____ network connects devices within a small geographical area such as an office building.",
          answer: "LAN (Local Area Network)"
        },
        { 
          id: 6, 
          topic: "Networking", 
          outcome: "Compare network types", 
          level: "Application", 
          type: "Matching", 
          count: 1, 
          marks: 4,
          sampleQuestion: "Match the network type with its characteristic.",
          answer: "LAN-Small area; WAN-Wide area; MAN-City-wide; Wi-Fi-Wireless"
        },
        { 
          id: 7, 
          topic: "Software Choices", 
          outcome: "Compare open-source vs. proprietary software", 
          level: "Application", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain two differences between open-source and proprietary software.",
          answer: "Open-source: free, source code accessible; Proprietary: paid, source code restricted"
        },
        { 
          id: 8, 
          topic: "Software Installation", 
          outcome: "Document the software installation process", 
          level: "Hands-on Practice", 
          type: "Practical Task", 
          count: 1, 
          marks: 6,
          sampleQuestion: "Describe the steps to install a software application on your computer.",
          answer: "1. Download installer, 2. Run setup, 3. Accept terms, 4. Choose install location, 5. Complete installation, 6. Verify"
        }
      ],
      distribution: { knowledge: 30, application: 40, criticalThinking: 30 },
      resources: ["Test Blueprint PDF", "Answer Key"]
    },
    unit21_test: {
      id: "unit21_test",
      title: "Unit 2.1: Digital Media Fundamentals Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 60,
      duration: "1 hour 30 minutes",
      topics: ["Digital Media Importance", "Media Devices", "Content Creation", "Software Tools"],
      items: [
        { 
          id: 1, 
          topic: "Digital Media Importance", 
          outcome: "Understand the importance of digital media", 
          level: "Understanding", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Which of the following best describes digital media?",
          answer: "Content created, distributed, and consumed in digital format"
        },
        { 
          id: 2, 
          topic: "Digital Media Importance", 
          outcome: "Explain significance for Vanuatu context", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain the significance of digital media for information sharing in Vanuatu.",
          answer: "Enables rapid communication across islands, supports education, promotes cultural preservation"
        },
        { 
          id: 3, 
          topic: "Media Devices", 
          outcome: "Identify necessary audio/video devices", 
          level: "Knowledge", 
          type: "Matching", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Match the device with its primary function.",
          answer: "Microphone-Audio input; Camera-Video capture; Speaker-Audio output; Projector-Visual display"
        },
        { 
          id: 4, 
          topic: "Media Devices", 
          outcome: "Understand device functions", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 2, 
          marks: 10,
          sampleQuestion: "Describe the necessary equipment for recording a cultural ceremony.",
          answer: "Video camera for visual, microphone for audio, tripod for stability, storage device"
        },
        { 
          id: 5, 
          topic: "Content Creation", 
          outcome: "Identify requirements for creating audio/video/graphic content", 
          level: "Application", 
          type: "Fill in the Blanks", 
          count: 5, 
          marks: 5,
          sampleQuestion: "For quality audio recording, you need a good _____ and a quiet _____.",
          answer: "microphone, environment"
        },
        { 
          id: 6, 
          topic: "Content Creation", 
          outcome: "Apply content creation principles", 
          level: "Application", 
          type: "Short Answer", 
          count: 3, 
          marks: 15,
          sampleQuestion: "Explain key requirements for creating engaging video content for Vanuatu audiences.",
          answer: "Cultural relevance, clear message, appropriate length, good quality, accessibility"
        },
        { 
          id: 7, 
          topic: "Software Tools", 
          outcome: "Understand basic software operations", 
          level: "Knowledge", 
          type: "True/False", 
          count: 5, 
          marks: 5,
          sampleQuestion: "True or False: You can edit photos using basic image editing software.",
          answer: "True"
        },
        { 
          id: 8, 
          topic: "Software Tools", 
          outcome: "Apply software knowledge in scenarios", 
          level: "Application", 
          type: "Scenario-based", 
          count: 2, 
          marks: 10,
          sampleQuestion: "Describe the steps to crop and save a photo for a poster.",
          answer: "1. Open image in editor, 2. Select crop tool, 3. Define crop area, 4. Apply crop, 5. Save file"
        }
      ],
      distribution: { knowledge: 25, understanding: 33, application: 42 },
      resources: ["Test Blueprint PDF", "Answer Key"]
    },
    unit22_test: {
      id: "unit22_test",
      title: "Unit 2.2: Web Design and Database Fundamentals Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 80,
      duration: "2 hours",
      topics: ["Web Design Requirements", "CSS", "HTML & CSS", "Database-Driven Websites", "Testing & Validation"],
      items: [
        { 
          id: 1, 
          topic: "Web Design Requirements", 
          outcome: "Understand key web design considerations", 
          level: "Understanding", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Which is most important for websites in Vanuatu?",
          answer: "Mobile responsiveness (due to high mobile usage)"
        },
        { 
          id: 2, 
          topic: "Web Design Requirements", 
          outcome: "Apply design principles for Vanuatu organizations", 
          level: "Application", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain key design considerations for a tourism website in Vanuatu.",
          answer: "Cultural imagery, mobile-friendly, fast loading, bilingual support, booking functionality"
        },
        { 
          id: 3, 
          topic: "CSS", 
          outcome: "Understand CSS syntax and concepts", 
          level: "Knowledge", 
          type: "Matching", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Match CSS terms with definitions.",
          answer: "Selector-targets element; Property-what to style; Value-how to style; Declaration-property+value"
        },
        { 
          id: 4, 
          topic: "CSS", 
          outcome: "Understand CSS syntax", 
          level: "Knowledge", 
          type: "Fill in the Blanks", 
          count: 5, 
          marks: 5,
          sampleQuestion: "In CSS, a _____ is used to select an HTML element to apply styles.",
          answer: "selector"
        },
        { 
          id: 5, 
          topic: "CSS", 
          outcome: "Explain CSS advantages and application", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 2, 
          marks: 10,
          sampleQuestion: "Explain two advantages of using CSS for website styling.",
          answer: "1. Separation of content and design, 2. Easier maintenance and updates across pages"
        },
        { 
          id: 6, 
          topic: "HTML & CSS", 
          outcome: "Identify and understand code functions", 
          level: "Understanding", 
          type: "Code Identification", 
          count: 5, 
          marks: 10,
          sampleQuestion: "Identify what this HTML code does: <img src='beach.jpg' alt='Beach'>",
          answer: "Displays an image from beach.jpg file with 'Beach' as alternative text"
        },
        { 
          id: 7, 
          topic: "HTML & CSS", 
          outcome: "Apply HTML/CSS to create webpage elements", 
          level: "Application", 
          type: "Practical Task", 
          count: 2, 
          marks: 10,
          sampleQuestion: "Describe the HTML and CSS needed to create a centered heading with blue text.",
          answer: "HTML: <h1 class='title'>Heading</h1>; CSS: .title {color: blue; text-align: center;}"
        },
        { 
          id: 8, 
          topic: "Database-Driven Websites", 
          outcome: "Understand database website concepts", 
          level: "Knowledge", 
          type: "True/False", 
          count: 5, 
          marks: 5,
          sampleQuestion: "True or False: Database-driven websites can store and retrieve user information.",
          answer: "True"
        },
        { 
          id: 9, 
          topic: "Database-Driven Websites", 
          outcome: "Explain database website benefits and operations", 
          level: "Analysis", 
          type: "Short Answer", 
          count: 3, 
          marks: 15,
          sampleQuestion: "Explain how a database-driven website benefits a Vanuatu business.",
          answer: "Dynamic content updates, customer data management, inventory tracking, online orders"
        },
        { 
          id: 10, 
          topic: "Testing & Validation", 
          outcome: "Understand testing importance and process", 
          level: "Understanding", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Why is website validation important?",
          answer: "Ensures code follows standards, improves compatibility, identifies errors"
        },
        { 
          id: 11, 
          topic: "Testing & Validation", 
          outcome: "Explain validation process", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Describe the steps to validate a website using W3C validator.",
          answer: "1. Go to W3C validator, 2. Enter URL or upload code, 3. Run validation, 4. Review errors, 5. Fix issues"
        }
      ],
      distribution: { knowledge: 31, understanding: 38, application: 19, analysis: 12 },
      resources: ["Test Blueprint PDF", "Answer Key"]
    },
    unit31_test: {
      id: "unit31_test",
      title: "Unit 3.1: Data Representation Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 50,
      duration: "1 hour",
      topics: ["Digital Representation", "Binary Arithmetic", "Logic Gates and Truth Tables"],
      items: [
        { 
          id: 1, 
          topic: "Digital Representation", 
          outcome: "Understand Unicode and its importance", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Which of the following is the primary benefit of using Unicode?",
          answer: "b) It provides a unique code for almost every character worldwide."
        },
        { 
          id: 2, 
          topic: "Digital Representation", 
          outcome: "Explain character encoding concepts", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 2, 
          marks: 6,
          sampleQuestion: "Briefly explain one limitation of older character encoding standards (like ASCII) and how Unicode overcomes this limitation.",
          answer: "ASCII was limited to 128/256 characters (English and basic symbols only). Unicode overcomes this by providing codes for characters from virtually all writing systems worldwide."
        },
        { 
          id: 3, 
          topic: "Digital Representation", 
          outcome: "Perform number base conversions", 
          level: "Application", 
          type: "Conversion Problems", 
          count: 2, 
          marks: 4,
          sampleQuestion: "Convert the binary number 100111 to its hexadecimal equivalent.",
          answer: "100111 → 10 0111 → 27 (hexadecimal)"
        },
        { 
          id: 4, 
          topic: "Binary Arithmetic", 
          outcome: "Perform binary addition", 
          level: "Application", 
          type: "Problem Solving", 
          count: 1, 
          marks: 4,
          sampleQuestion: "Perform the following binary addition: 10110 + 01101",
          answer: "10110 + 01101 = 100011"
        },
        { 
          id: 5, 
          topic: "Binary Arithmetic", 
          outcome: "Perform binary subtraction using 2's complement", 
          level: "Application", 
          type: "Problem Solving", 
          count: 1, 
          marks: 4,
          sampleQuestion: "Perform the following binary subtraction using 2's complement: 1110 - 0110. Show your steps.",
          answer: "2's complement of 0110 is 1010. 1110 + 1010 = 11000. Discard carry → 1000 (result is 8)"
        },
        { 
          id: 6, 
          topic: "Binary Arithmetic", 
          outcome: "Perform binary multiplication using shift and add", 
          level: "Application", 
          type: "Problem Solving", 
          count: 1, 
          marks: 4,
          sampleQuestion: "Perform binary multiplication using shift and add: 111 × 10. Show your steps.",
          answer: "111 × 10 = 111 (bit 1) + 0000 (bit 0) = 1110 (14 in decimal)"
        },
        { 
          id: 7, 
          topic: "Binary Arithmetic", 
          outcome: "Understand binary arithmetic concepts", 
          level: "Knowledge", 
          type: "True/False", 
          count: 3, 
          marks: 3,
          sampleQuestion: "In binary addition, 1 + 1 equals 1. (True / False)",
          answer: "False (1 + 1 = 10 in binary)"
        },
        { 
          id: 8, 
          topic: "Logic Gates", 
          outcome: "Match logic gate symbols with names and functions", 
          level: "Knowledge", 
          type: "Matching", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Match the logic gate symbol with its name and function.",
          answer: "AND-Output HIGH if all inputs HIGH; OR-Output HIGH if any input HIGH; NOT-Inverts input"
        },
        { 
          id: 9, 
          topic: "Logic Gates", 
          outcome: "Complete truth tables for logic gates", 
          level: "Application", 
          type: "Truth Table Completion", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Complete the truth table for a 2-input NAND gate.",
          answer: "Inputs: 00→1, 01→1, 10→1, 11→0 (NAND is inverted AND)"
        },
        { 
          id: 10, 
          topic: "Logic Gates", 
          outcome: "Analyze logic circuits and determine outputs", 
          level: "Analysis", 
          type: "Logic Circuit Analysis", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Given a circuit with NOT gate on input A, then AND with input B. Determine outputs for all input combinations.",
          answer: "A=0,B=0→0; A=0,B=1→1; A=1,B=0→0; A=1,B=1→0"
        },
        { 
          id: 11, 
          topic: "Logic Gates", 
          outcome: "Explain logic gate functions and applications", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain the function of an OR gate. Provide an example of a real-world scenario where an OR gate's logic might be useful.",
          answer: "OR gate outputs HIGH if any input is HIGH. Example: A burglar alarm that triggers if door sensor OR window sensor detects intrusion."
        }
      ],
      distribution: { knowledge: 26, understanding: 22, application: 34, analysis: 10 },
      resources: ["Test Blueprint PDF", "Test Questions PDF", "Notes and Assessments PDF", "Answer Key PDF"]
    },
    unit32_test: {
      id: "unit32_test",
      title: "Unit 3.2: Introduction to Programming Concepts Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 60,
      duration: "1 hour",
      topics: ["Programming Lifecycle", "Problem-Solving & Design", "Representing Solutions", "Coding & Modular Programming"],
      items: [
        { 
          id: 1, 
          topic: "Programming Stages", 
          outcome: "Knowledge of programming purpose and stages", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "What is the main purpose of programming?",
          answer: "b) Create instructions for computers to perform tasks"
        },
        { 
          id: 2, 
          topic: "Programming Lifecycle", 
          outcome: "Define and explain programming with context", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Define programming and give examples relevant to Vanuatu.",
          answer: "Programming is writing instructions for computers. Examples: agricultural apps, remote communication, educational resources."
        },
        { 
          id: 3, 
          topic: "Program Stages", 
          outcome: "Explain program design and documentation", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain Program Design and Program Documentation stages.",
          answer: "Design: planning solution with algorithms. Documentation: written explanation of program purpose and usage."
        },
        { 
          id: 4, 
          topic: "Design Approaches", 
          outcome: "Match design concepts", 
          level: "Knowledge", 
          type: "Matching", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Match design approaches with descriptions.",
          answer: "Top-Down, Bottom-Up, Modular, etc."
        },
        { 
          id: 5, 
          topic: "Problem-Solving", 
          outcome: "Explain top-down approach benefits", 
          level: "Analysis", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain how top-down is beneficial for complex systems.",
          answer: "Breaks complex problems into manageable sub-tasks, easier to manage complexity."
        },
        { 
          id: 6, 
          topic: "Problem-Solving", 
          outcome: "Describe problem identification process", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain the process of identifying and categorizing problems.",
          answer: "Recognize need, understand problem type, break into components."
        },
        { 
          id: 7, 
          topic: "Pseudocode", 
          outcome: "Analyze pseudocode logic", 
          level: "Analysis", 
          type: "Code Analysis", 
          count: 1, 
          marks: 7,
          sampleQuestion: "Explain what the given pseudocode does.",
          answer: "Compares two numbers and outputs the larger one."
        },
        { 
          id: 8, 
          topic: "Flowcharts", 
          outcome: "Create flowcharts", 
          level: "Application", 
          type: "Diagram Creation", 
          count: 1, 
          marks: 8,
          sampleQuestion: "Draw flowchart for voting eligibility (age >= 18).",
          answer: "START → Input Age → Decision (Age>=18) → Output messages → END"
        },
        { 
          id: 9, 
          topic: "Coding", 
          outcome: "Define coding", 
          level: "Knowledge", 
          type: "Short Answer", 
          count: 1, 
          marks: 4,
          sampleQuestion: "What is coding?",
          answer: "Writing instructions in programming language that computer can execute."
        },
        { 
          id: 10, 
          topic: "Modular Programming", 
          outcome: "Explain benefits of modularity", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 4,
          sampleQuestion: "Explain two key benefits of modular programming.",
          answer: "Organization/readability and code reusability."
        },
        { 
          id: 11, 
          topic: "Modular Programming", 
          outcome: "Analyze modular code structure", 
          level: "Analysis", 
          type: "Code Analysis", 
          count: 1, 
          marks: 7,
          sampleQuestion: "Identify modular elements and explain benefits.",
          answer: "Functions (calculateArea, calculatePerimeter) provide organization, reusability, readability."
        }
      ],
      distribution: { knowledge: 23, understanding: 32, application: 13, analysis: 32 },
      resources: ["Test Blueprint PDF", "Test Questions PDF", "Notes and Assessments PDF", "Answer Key PDF"]
    },
    unit33_test: {
      id: "unit33_test",
      title: "Unit 3.3: Microprocessors and Embedded Systems Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 70,
      duration: "1.5 hours",
      topics: ["Microprocessor Architecture", "Code to Execution", "Programming & Interfacing", "Embedded Systems"],
      items: [
        { 
          id: 1, 
          topic: "Microprocessor Components", 
          outcome: "Identify microprocessor components and functions", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Which component performs arithmetic operations?",
          answer: "c) ALU"
        },
        { 
          id: 2, 
          topic: "ALU Function", 
          outcome: "Explain ALU operations", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain the primary function of the ALU with examples.",
          answer: "Performs arithmetic and logical operations like addition, AND, OR, comparisons."
        },
        { 
          id: 3, 
          topic: "System Bus", 
          outcome: "Describe System Bus role", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Describe the role of the System Bus.",
          answer: "Communication channel connecting microprocessor to memory via Address, Data, and Control buses."
        },
        { 
          id: 4, 
          topic: "Component Interaction", 
          outcome: "Explain component coordination", 
          level: "Analysis", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Why is interaction between Control Unit, ALU, and Registers essential?",
          answer: "Control Unit directs, ALU computes, Registers store - coordinated flow enables instruction execution."
        },
        { 
          id: 5, 
          topic: "Code Translation", 
          outcome: "Match translation tools", 
          level: "Knowledge", 
          type: "Matching", 
          count: 5, 
          marks: 5,
          sampleQuestion: "Match: Compiler, Interpreter, Assembler, Machine Code, Assembly Language",
          answer: "a-iv, b-i, c-ii, d-v, e-iii (from context)"
        },
        { 
          id: 6, 
          topic: "Code Conversion Process", 
          outcome: "Describe conversion process", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Describe the process of converting high-level code to machine code.",
          answer: "High-level code written → Compiler/Interpreter translates → Machine code executed by processor."
        },
        { 
          id: 7, 
          topic: "Assembler", 
          outcome: "Explain assembler purpose", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain the purpose of an assembler.",
          answer: "Translates assembly language (mnemonics) to machine code for low-level programming."
        },
        { 
          id: 8, 
          topic: "Microprocessor Control", 
          outcome: "Write control instructions", 
          level: "Application", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Present three-step instructions to control LED based on switch.",
          answer: "1) Read switch state, 2) If closed turn LED ON, 3) Else turn LED OFF."
        },
        { 
          id: 9, 
          topic: "Sensors and Actuators", 
          outcome: "Explain sensor/actuator roles", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Explain the role of sensors and actuators with example.",
          answer: "Sensors detect (temperature), actuators respond (fan). Microprocessor processes and controls."
        },
        { 
          id: 10, 
          topic: "Sensor Interfacing", 
          outcome: "Describe interfacing process", 
          level: "Application", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Describe interfacing a temperature sensor with a microprocessor.",
          answer: "Sensor outputs analog voltage → ADC converts to digital → Microprocessor reads and processes."
        },
        { 
          id: 11, 
          topic: "Embedded Systems", 
          outcome: "Knowledge of embedded concepts", 
          level: "Knowledge", 
          type: "Multiple Choice", 
          count: 5, 
          marks: 5,
          sampleQuestion: "An embedded microprocessor is designed for:",
          answer: "b) Specific task within a larger system"
        },
        { 
          id: 12, 
          topic: "Embedded Systems", 
          outcome: "Define embedded systems", 
          level: "Understanding", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Define embedded system and provide Vanuatu examples.",
          answer: "Specialized computer for dedicated function. Examples: Mobile phones, solar inverters, digital TVs."
        },
        { 
          id: 13, 
          topic: "Programmable Microprocessors", 
          outcome: "Explain importance", 
          level: "Analysis", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Why are programmable microprocessors essential for embedded devices?",
          answer: "Flexibility through software, automation of tasks, adaptability to different needs."
        },
        { 
          id: 14, 
          topic: "Software Reliability", 
          outcome: "Discuss error consequences", 
          level: "Analysis", 
          type: "Short Answer", 
          count: 1, 
          marks: 5,
          sampleQuestion: "Discuss consequences of software errors in medical monitoring devices.",
          answer: "Inaccurate readings → wrong diagnosis, malfunction during critical period → life-threatening."
        }
      ],
      distribution: { knowledge: 21, understanding: 36, application: 14, analysis: 29 },
      resources: ["Test Blueprint PDF", "Test Questions PDF", "Circuit Diagrams PDF", "Mnemonic Codes Notes PDF", "Answer Key PDF"]
    },
    unit41_test: {
      id: "unit41_test",
      title: "Unit 4.1: Cybercrime and Cybersecurity Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 100,
      duration: "2 hours",
      topics: ["Cybercrime Definitions", "Cybercrime Landscape", "Legal Measures", "Challenges", "Cybersecurity Guidelines"],
      items: [
        {
          id: 1,
          topic: "Definitions and Concepts - MCQ",
          outcome: "Define cybercrime and cyber legislation",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 5,
          marks: 10,
          sampleQuestion: "What is cybercrime? a) Physical crime b) Criminal activity targeting computers/networks c) Software development d) Internet marketing",
          answer: "b) Criminal activity targeting computers, networks, or networked devices"
        },
        {
          id: 2,
          topic: "Definitions and Concepts - Fill-in-Blank",
          outcome: "Identify key cybersecurity terms",
          level: "Knowledge",
          type: "Fill in the Blank",
          count: 5,
          marks: 10,
          sampleQuestion: "_____ is a term for individuals who illegally access computer systems.",
          answer: "Hackers"
        },
        {
          id: 3,
          topic: "Cybercrime Landscape - True/False",
          outcome: "Identify cybercrime concerns in ICT",
          level: "Knowledge",
          type: "True/False",
          count: 5,
          marks: 5,
          sampleQuestion: "True or False: Ransomware attacks are a growing cybercrime concern.",
          answer: "True"
        },
        {
          id: 4,
          topic: "Cybercrime Landscape - Short Answer",
          outcome: "Explain reported cybercrimes in Vanuatu",
          level: "Understanding",
          type: "Short Answer",
          count: 4,
          marks: 20,
          sampleQuestion: "List two common cybercrime concerns in ICT with examples from Vanuatu or Pacific.",
          answer: "Ransomware attacks, data breaches, phishing, cyberbullying, online defamation (any two with examples)"
        },
        {
          id: 5,
          topic: "Legal Measures - MCQ",
          outcome: "Identify key components of cyber legislation",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 5,
          marks: 10,
          sampleQuestion: "What is a key component of cyber legislation? a) Software development b) Definitions and penalties for cyber offenses c) Marketing strategies",
          answer: "b) Definitions and penalties for cyber offenses"
        },
        {
          id: 6,
          topic: "Legal Measures - Short Answer",
          outcome: "Explain efforts to combat cybercrimes",
          level: "Understanding",
          type: "Short Answer",
          count: 3,
          marks: 15,
          sampleQuestion: "Describe the role of CERT Vanuatu in combating cybercrimes.",
          answer: "Computer Emergency Response Team responds to security incidents, provides guidance, coordinates cybersecurity efforts, conducts training."
        },
        {
          id: 7,
          topic: "Challenges - Short Answer",
          outcome: "Analyze challenges for Pacific Island Nations",
          level: "Analysis",
          type: "Short Answer",
          count: 2,
          marks: 10,
          sampleQuestion: "List and explain two challenges Pacific Island Nations face in combating cybercrimes.",
          answer: "Lack of proper legislation, limited technical capacity, resource constraints, low awareness levels (any two with explanations)"
        },
        {
          id: 8,
          topic: "Challenges - Essay Question",
          outcome: "Evaluate impacts and challenges",
          level: "Analysis",
          type: "Essay (Choice 1 of 2)",
          count: 1,
          marks: 10,
          sampleQuestion: "Discuss the impact of cybersecurity attacks on Vanuatu businesses OR Analyze challenges in implementing cyber legislation in the Pacific.",
          answer: "Financial loss, operational disruption, reputational damage, data breach consequences, recovery costs, customer trust impact (or) Legislative gaps, technical capacity, resources, awareness, enforcement challenges, international coordination needs"
        },
        {
          id: 9,
          topic: "Cybersecurity Guidelines - Fill-in-Blank",
          outcome: "Recall key security guidelines",
          level: "Knowledge",
          type: "Fill in the Blank",
          count: 5,
          marks: 5,
          sampleQuestion: "Using a _____ password is a key preventative measure.",
          answer: "strong"
        },
        {
          id: 10,
          topic: "Cybersecurity Guidelines - Short Answer",
          outcome: "Explain preventative measures",
          level: "Understanding",
          type: "Short Answer",
          count: 1,
          marks: 5,
          sampleQuestion: "Explain why multi-factor authentication (MFA) is an effective security measure.",
          answer: "Adds extra verification layer beyond password, requires multiple forms of proof (password + code), protects even if password is compromised, significantly reduces unauthorized access risk."
        }
      ],
      distribution: { knowledge: 30, understanding: 40, application: 0, analysis: 20 },
      resources: ["Blueprint PDF", "Lesson Notes PDF", "CERT Vanuatu Information", "Vanuatu Harmful Digital Communications Act", "Cybersecurity Tools List"]
    },
    unit42_test: {
      id: "unit42_test",
      title: "Unit 4.2: Ethical Measures in ICT Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 100,
      duration: "2 hours",
      topics: ["Cybercrime Definitions", "Cybercrime Landscape", "Legal & Preventative Measures", "Challenges & Impacts", "Cybersecurity Guidelines"],
      items: [
        {
          id: 1,
          topic: "Definitions - MCQ",
          outcome: "Define cybercrime and cyber legislation",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 5,
          marks: 10,
          sampleQuestion: "Which of the following best defines cybercrime? a) Criminal activity online b) Criminal activity targeting computers/networks c) Illegal internet activity",
          answer: "b) Criminal activity targeting or using computers, networks, or networked devices"
        },
        {
          id: 2,
          topic: "Definitions - Fill-in-Blank",
          outcome: "Identify key cybersecurity terms",
          level: "Knowledge",
          type: "Fill in the Blank",
          count: 5,
          marks: 10,
          sampleQuestion: "Criminal activity targeting computers is known as _____.",
          answer: "cybercrime"
        },
        {
          id: 3,
          topic: "Cybercrime Landscape - True/False",
          outcome: "Identify cybercrime concerns",
          level: "Knowledge",
          type: "True/False",
          count: 5,
          marks: 5,
          sampleQuestion: "True or False: Cyberbullying is not a reported concern in Vanuatu.",
          answer: "False"
        },
        {
          id: 4,
          topic: "Cybercrime Landscape - Short Answer",
          outcome: "Explain cybercrime concerns in Vanuatu context",
          level: "Understanding",
          type: "Short Answer",
          count: 4,
          marks: 20,
          sampleQuestion: "Describe one growing concern related to cybercrime affecting citizens or institutions in Vanuatu.",
          answer: "Social media crimes like cyberbullying and online defamation; vulnerability of institutions due to limited resources; ransomware attacks"
        },
        {
          id: 5,
          topic: "Legal Measures - MCQ",
          outcome: "Identify key components of cyber legislation",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 5,
          marks: 10,
          sampleQuestion: "A key component of cyber legislation includes: a) Free internet b) Defining offenses and penalties c) Promoting social media",
          answer: "b) Defining cyber offenses and their penalties"
        },
        {
          id: 6,
          topic: "Legal & Preventative Measures - Short Answer",
          outcome: "Explain efforts to combat cybercrimes",
          level: "Understanding",
          type: "Short Answer",
          count: 3,
          marks: 15,
          sampleQuestion: "State one key component of cyber legislation in Vanuatu.",
          answer: "Definitions of specific cyber offenses and penalties for committing them"
        },
        {
          id: 7,
          topic: "Challenges & Impacts - Short Answer",
          outcome: "Analyze challenges for Pacific Island Nations",
          level: "Analysis",
          type: "Short Answer",
          count: 2,
          marks: 10,
          sampleQuestion: "Identify one significant challenge faced by Pacific Island nations in combating cybercrime.",
          answer: "Lack of proper legislation, limited technical capacity, resource constraints, low awareness"
        },
        {
          id: 8,
          topic: "Challenges & Impacts - Essay",
          outcome: "Evaluate impacts and challenges",
          level: "Analysis",
          type: "Essay (Choose 1 of 2)",
          count: 1,
          marks: 10,
          sampleQuestion: "Discuss key challenges Pacific Island nations face in combating cybercrime OR Explain potential effects of cyber-attacks on Vanuatu organizations.",
          answer: "Challenges: legislation gaps, technical capacity, resources, awareness, enforcement. Impacts: financial loss, operational disruption, reputational damage, data breach"
        },
        {
          id: 9,
          topic: "Cybersecurity Guidelines - Fill-in-Blank",
          outcome: "Recall key security guidelines",
          level: "Knowledge",
          type: "Fill in the Blank",
          count: 5,
          marks: 5,
          sampleQuestion: "Organizations should implement strong _____ policies.",
          answer: "password"
        },
        {
          id: 10,
          topic: "Cybersecurity Guidelines - Short Answer",
          outcome: "Explain cybersecurity guidelines for organizations",
          level: "Understanding",
          type: "Short Answer",
          count: 1,
          marks: 5,
          sampleQuestion: "Explain one key cybersecurity guideline organizations in Vanuatu should implement.",
          answer: "Strong password policies, regular software updates, cybersecurity awareness training, data backups"
        }
      ],
      distribution: { knowledge: 30, understanding: 40, application: 0, analysis: 20 },
      resources: ["Test Blueprint PDF", "Lesson Notes PDF", "Harmful Digital Communications Act 2024", "CERT Vanuatu Resources", "Pacific Cybersecurity Initiatives"]
    },
    unit43_test: {
      id: "unit43_test",
      title: "Unit 4.3: Environmental Impact of ICT Test Blueprint",
      type: "Summative Assessment",
      totalMarks: 100,
      duration: "2 hours",
      topics: ["ICT Waste & Environmental Impact", "ICT Electricity Usage", "ICT Waste Management", "Sustainable Technology"],
      items: [
        {
          id: 1,
          topic: "ICT Waste - Multiple Choice",
          outcome: "Identify physical waste problems created by ICT",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 5,
          marks: 10,
          sampleQuestion: "Which of the following is the most significant physical waste problem created by ICT? a) Paper waste b) Plastic packaging c) E-waste d) Batteries",
          answer: "c) Electronic waste (e-waste)"
        },
        {
          id: 2,
          topic: "ICT Waste - Fill-in-Blank",
          outcome: "Recall key e-waste terminology",
          level: "Knowledge",
          type: "Fill in the Blank",
          count: 5,
          marks: 10,
          sampleQuestion: "The term for discarded electronic devices is _____.",
          answer: "e-waste"
        },
        {
          id: 3,
          topic: "Environmental Disposal Impacts - True/False",
          outcome: "Identify environmental concerns of e-waste disposal",
          level: "Knowledge",
          type: "True/False",
          count: 5,
          marks: 5,
          sampleQuestion: "True or False: Burning e-waste can release fine particles into the air.",
          answer: "True"
        },
        {
          id: 4,
          topic: "Environmental Disposal Impacts - Short Answer",
          outcome: "Explain environmental effects of improper e-waste disposal",
          level: "Understanding",
          type: "Short Answer",
          count: 4,
          marks: 20,
          sampleQuestion: "Explain two ways e-waste disposal can contaminate soil and groundwater in island nations like Vanuatu.",
          answer: "Leaching of toxic substances (lead, mercury, cadmium) into soil/groundwater; acidic runoff accelerating dissolution of heavy metals"
        },
        {
          id: 5,
          topic: "ICT Electricity Usage - Multiple Choice",
          outcome: "Identify environmental impacts of ICT electricity consumption",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 4,
          marks: 8,
          sampleQuestion: "What is the primary environmental impact of using fossil fuel electricity for ICT? a) Oxygen depletion b) Greenhouse gas emissions c) Ozone depletion",
          answer: "b) Contribution to climate change through greenhouse gas emissions"
        },
        {
          id: 6,
          topic: "ICT Electricity Usage - Short Answer",
          outcome: "Explain electricity consumption impacts",
          level: "Understanding",
          type: "Short Answer",
          count: 2,
          marks: 12,
          sampleQuestion: "How does ICT electricity consumption contribute to climate change?",
          answer: "ICT devices consume electricity often generated from fossil fuels, releasing CO2 and greenhouse gases that trap heat and cause global warming"
        },
        {
          id: 7,
          topic: "ICT Waste Management - Fill-in-Blank",
          outcome: "Recall waste management measures",
          level: "Knowledge",
          type: "Fill in the Blank",
          count: 6,
          marks: 6,
          sampleQuestion: "Establishing designated _____ points makes it easier for communities to dispose of e-waste responsibly.",
          answer: "collection"
        },
        {
          id: 8,
          topic: "ICT Waste Management - Short Answer",
          outcome: "Describe effective waste management measures",
          level: "Understanding",
          type: "Short Answer",
          count: 3,
          marks: 18,
          sampleQuestion: "Describe an effective measure for managing ICT waste locally in Vanuatu.",
          answer: "Establishing collection points, repair/reuse initiatives, partnerships with recycling organizations, awareness campaigns"
        },
        {
          id: 9,
          topic: "Sustainable Technology - Multiple Choice",
          outcome: "Define sustainable technology concepts",
          level: "Knowledge",
          type: "Multiple Choice",
          count: 3,
          marks: 6,
          sampleQuestion: "Sustainable technology is best defined as technology that considers: a) Only economic benefits b) Only immediate needs c) Environmental, social, and economic factors",
          answer: "c) Environmental, social, and economic factors"
        },
        {
          id: 10,
          topic: "Sustainable Technology - Short Answer",
          outcome: "Explain sustainable technology examples and measures",
          level: "Understanding",
          type: "Short Answer",
          count: 2,
          marks: 6,
          sampleQuestion: "State one example of sustainable technology relevant to ICT in Vanuatu.",
          answer: "Solar-powered charging stations, energy-efficient devices, cloud computing with renewables, LED lighting"
        }
      ],
      distribution: { knowledge: 35, understanding: 45, application: 0, analysis: 20 },
      resources: ["Test Blueprint PDF", "Environmental Impact Notes", "Sustainable Technology Guidelines", "RecycleCorp Vanuatu Information", "Basel Convention Resources"]
    }
  };

  const currentBlueprint = blueprints[selectedBlueprint] || blueprints.unit11_test;

  // Get all available classes
  // For teachers, use API-returned classes directly; for admins, use all hardcoded classes
  const availableClasses = currentUser && currentUser.role === 'teacher' 
    ? teacherClasses.map(c => c.name)  // Use API classes directly for teachers
    : (currentUser && (currentUser.role === 'admin' || currentUser.role === 'principal'))
      ? accessibleClasses  // Admins see all hardcoded classes
      : [];

  // Show loading state while fetching classes
  if (loading) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading curriculum data...</p>
        </div>
      </div>
    );
  }

  // Show message if no classes available
  if (availableClasses.length === 0) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">No classes assigned to you.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-primary" />
            Curriculum Manager 2026
          </h1>
          <p className="text-muted-foreground mt-1">
            Strategic planning and assessment management for all classes
          </p>
        </div>
        <div className="flex gap-2">
           <Button variant="outline" className="gap-2">
             <ScrollText className="h-4 w-4" />
             Export Plans
           </Button>
           <Button className="gap-2">
             <Plus className="h-4 w-4" />
             Add New Plan
           </Button>
        </div>
      </div>

      {/* Class Selector */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900 border-2">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <School className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">Select Class:</span>
            </div>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-[300px] bg-white dark:bg-slate-950">
                <SelectValue placeholder="Select a class" />
              </SelectTrigger>
              <SelectContent>
                {availableClasses.map((className) => (
                  <SelectItem key={className} value={className}>
                    {className}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="bg-white dark:bg-slate-950">
              {selectedClass.match(/Year (\d+)/)?.[1] ? `Year ${selectedClass.match(/Year (\d+)/)?.[1]}` : 'N/A'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:w-[1200px] bg-gradient-to-r from-slate-100 to-slate-50 p-1">
          <TabsTrigger 
            value="overview" 
            className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
          >
            <PieChart className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger 
            value="planner" 
            className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
          >
            <CalendarDays className="h-4 w-4" /> Year Planner
          </TabsTrigger>
          <TabsTrigger 
            value="scheme" 
            className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
          >
            <Layers className="h-4 w-4" /> Scheme of Work
          </TabsTrigger>
          <TabsTrigger 
            value="units" 
            className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
          >
            <FileText className="h-4 w-4" /> Unit Plans
          </TabsTrigger>
          <TabsTrigger 
            value="blueprints" 
            className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
          >
            <Target className="h-4 w-4" /> Blueprints
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - Dynamic Data from API */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {loadingCurriculum ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading curriculum data...</p>
              </div>
            </div>
          ) : curriculumDataAPI ? (
            <div className="space-y-6">
              {/* Statistics Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Assessments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{curriculumDataAPI.statistics.total_assessments}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Marks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{curriculumDataAPI.statistics.total_marks}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Terms</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{curriculumDataAPI.statistics.term_count}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Terms Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Curriculum Overview: {curriculumDataAPI.class.name}</CardTitle>
                  <CardDescription>Assessment distribution across terms</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {curriculumDataAPI.terms.map((term, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-primary">{term.term}</Badge>
                            <h3 className="font-semibold">{term.assessment_count} Assessments</h3>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Total Marks</p>
                            <p className="text-2xl font-bold text-primary">{term.total_marks}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <Progress value={(term.estimated_weeks / 14) * 100} className="flex-1" />
                          <span className="text-sm text-muted-foreground">{term.estimated_weeks} / 14 weeks</span>
                        </div>

                        <div className="mt-3">
                          <details className="group">
                            <summary className="cursor-pointer text-sm font-medium text-primary hover:underline flex items-center gap-2">
                              View Assessments ({term.assessment_count})
                              <ChevronRight className="h-4 w-4 group-open:rotate-90 transition-transform" />
                            </summary>
                            <div className="mt-3 space-y-2 pl-4">
                              {term.assessments.slice(0, 10).map((assessment, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                  <span className="text-sm">{assessment.title}</span>
                                  <Badge variant="outline">{assessment.marks} marks</Badge>
                                </div>
                              ))}
                              {term.assessments.length > 10 && (
                                <p className="text-xs text-muted-foreground italic">
                                  + {term.assessments.length - 10} more assessments
                                </p>
                              )}
                            </div>
                          </details>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Strands Overview */}
              {curriculumDataAPI.strands && curriculumDataAPI.strands.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Strands & Units</CardTitle>
                    <CardDescription>Curriculum structure by strand</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {curriculumDataAPI.strands.map((strand, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <Badge variant="outline" className="mb-2">Strand {strand.number}</Badge>
                              <h4 className="font-semibold">{strand.title}</h4>
                            </div>
                            <div className="text-right text-sm">
                              <p className="text-muted-foreground">{strand.assessment_count} assessments</p>
                              <p className="font-semibold">{strand.total_marks} marks</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {strand.terms.map((termName, tIdx) => (
                              <Badge key={tIdx} variant="secondary" className="text-xs">{termName}</Badge>
                            ))}
                          </div>
                          {strand.units && strand.units.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-muted-foreground">Units: {strand.units.join(', ')}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">No curriculum data available. Please select a class.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Year Planner Tab - Using Embedded Component */}
        <TabsContent value="planner" className="mt-6 space-y-6 p-6 rounded-lg bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50">
          <EmbeddedYearlyPlanner />
        </TabsContent>

        {/* Scheme of Work Tab - Using Embedded Component */}
        <TabsContent value="scheme" className="mt-6 p-6 rounded-lg bg-gradient-to-br from-emerald-50 via-green-50 to-lime-50">
          <EmbeddedSchemeOfWork />
        </TabsContent>

        {/* Unit Plans Tab - Using Embedded Component */}
        <TabsContent value="units" className="mt-6 p-6 rounded-lg bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50">
          <EmbeddedUnitPlans />
        </TabsContent>

        {/* Assessment Blueprints Tab - Dynamic */}
        <TabsContent value="blueprints" className="mt-6 p-6 rounded-lg bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
          {loadingCurriculum ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : curriculumDataAPI ? (
            <DynamicBlueprintsTab curriculumData={curriculumDataAPI} />
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">Please select a class to view assessment blueprints.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
